import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftRight,
  Building2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  FileText,
  Send,
  RefreshCw,
  Info,
  CreditCard,
  Smartphone,
  Building,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  X,
  Check,
  Plus,
  DollarSign,
  Shield,
  Banknote
} from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleSettlements = () => {
  const { customer } = useCustomerAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('positions')
  const [mgPosition, setMgPosition] = useState(null)
  const [floatLimit, setFloatLimit] = useState(null)
  const [settlementBatches, setSettlementBatches] = useState([])
  const [ufccCommission, setUfccCommission] = useState({ pending: 0, paid: 0, total: 0, transactions: [] })
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showUfccSettleModal, setShowUfccSettleModal] = useState(false)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [selectedCounterparty, setSelectedCounterparty] = useState(null)
  const [expandedBatch, setExpandedBatch] = useState(null)
  const [batchFilter, setBatchFilter] = useState('all')
  const [actionLoading, setActionLoading] = useState(false)
  
  // Form states
  const [confirmForm, setConfirmForm] = useState({
    bank_reference: '',
    mobile_money_reference: '',
    payment_method: 'bank'
  })
  const [ufccSettleForm, setUfccSettleForm] = useState({
    amount: 0,
    bank_reference: '',
    mobile_money_reference: '',
    payment_method: 'bank'
  })

  // Extract MG code from tenant DB
  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) return tenantDb.substring(prefix.length)
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const mgKey = getMgCode(customer?.tenantDb) || 'demo'

  useEffect(() => {
    if (mgKey) fetchAllData()
  }, [mgKey])

  const fetchAllData = async () => {
    try {
      setLoading(true)
      setError('')

      const [positionRes, floatRes, batchesRes] = await Promise.all([
        apiService.getKataleMgPosition(mgKey),
        apiService.checkKataleFloatLimit(mgKey),
        apiService.getKataleSettlementBatches(mgKey)
      ])

      if (positionRes.status === 'SUCCESS') setMgPosition(positionRes.data)
      if (floatRes.status === 'SUCCESS') setFloatLimit(floatRes.data)
      if (batchesRes.status === 'SUCCESS') setSettlementBatches(batchesRes.data || [])

      // Fetch UFCC commission
      try {
        const commRes = await apiService.getKataleUfccCommission(mgKey)
        if (commRes.status === 'SUCCESS') setUfccCommission(commRes.data)
      } catch (e) {
        console.error('Commission fetch error:', e)
      }
    } catch (err) {
      console.error('Settlement data error:', err)
      setError('Failed to load settlement data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-UG', {
      year: 'numeric', month: 'short', day: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-UG', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  // --- Create Settlement Batch ---
  const handleCreateBatch = async () => {
    if (!selectedCounterparty) return
    setActionLoading(true)
    try {
      const response = await apiService.createKataleSettlementBatch(mgKey, selectedCounterparty.counterparty_mg_code)
      if (response.status === 'SUCCESS') {
        setShowCreateBatchModal(false)
        setSelectedCounterparty(null)
        fetchAllData()
      } else {
        alert('Failed: ' + (response.message || 'Unknown error'))
      }
    } catch (err) {
      alert('Error creating batch: ' + err.message)
    } finally {
      setActionLoading(false)
    }
  }

  // --- Confirm Settlement Batch ---
  const handleConfirmSettlement = async () => {
    if (!selectedBatch) return
    const reference = confirmForm.payment_method === 'bank'
      ? confirmForm.bank_reference
      : confirmForm.mobile_money_reference
    if (!reference) {
      alert('Please enter a payment reference')
      return
    }
    setActionLoading(true)
    try {
      const response = await apiService.confirmKataleSettlement(
        selectedBatch.id,
        mgKey,
        confirmForm.payment_method === 'bank' ? confirmForm.bank_reference : null,
        confirmForm.payment_method === 'mobile_money' ? confirmForm.mobile_money_reference : null
      )
      if (response.status === 'SUCCESS') {
        setShowConfirmModal(false)
        setSelectedBatch(null)
        setConfirmForm({ bank_reference: '', mobile_money_reference: '', payment_method: 'bank' })
        fetchAllData()
      } else {
        alert('Failed: ' + (response.message || 'Unknown error'))
      }
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setActionLoading(false)
    }
  }

  // --- Settle UFCC Commission ---
  const handleSettleUfcc = async () => {
    const reference = ufccSettleForm.payment_method === 'bank'
      ? ufccSettleForm.bank_reference
      : ufccSettleForm.mobile_money_reference
    if (!reference) {
      alert('Please enter a payment reference')
      return
    }
    if (!ufccSettleForm.amount || ufccSettleForm.amount <= 0) {
      alert('Please enter a valid amount')
      return
    }
    setActionLoading(true)
    try {
      const response = await apiService.makeAuthenticatedRequest('KATALE-SETTLE-UFCC-COMMISSION', {
        mg_key: mgKey,
        amount: ufccSettleForm.amount,
        bank_reference: ufccSettleForm.payment_method === 'bank' ? ufccSettleForm.bank_reference : null,
        mobile_money_reference: ufccSettleForm.payment_method === 'mobile_money' ? ufccSettleForm.mobile_money_reference : null
      }, true)
      if (response.status === 'SUCCESS') {
        setShowUfccSettleModal(false)
        setUfccSettleForm({ amount: 0, bank_reference: '', mobile_money_reference: '', payment_method: 'bank' })
        fetchAllData()
      } else {
        alert('Failed: ' + (response.message || 'Unknown error'))
      }
    } catch (err) {
      alert('Error: ' + err.message)
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" /> },
      partial: { bg: 'bg-blue-100 text-blue-800', icon: <ArrowLeftRight className="w-3 h-3" /> },
      settled: { bg: 'bg-green-100 text-green-800', icon: <CheckCircle2 className="w-3 h-3" /> },
      disputed: { bg: 'bg-red-100 text-red-800', icon: <AlertTriangle className="w-3 h-3" /> },
      confirmed: { bg: 'bg-green-100 text-green-800', icon: <Check className="w-3 h-3" /> }
    }
    const c = config[status] || { bg: 'bg-gray-100 text-gray-800', icon: null }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg}`}>
        {c.icon}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    )
  }

  // --- Loading State ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading settlement data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <ArrowLeftRight className="h-6 w-6 text-blue-600 mr-2" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">MG Settlements</h1>
                <p className="text-sm text-gray-500">Cross-MG transactions & UFCC commission for <span className="font-medium text-blue-600">{mgKey}</span></p>
              </div>
            </div>
            <button
              onClick={fetchAllData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Net Position */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">Net Position</span>
              {(mgPosition?.net_position || 0) >= 0 ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
            </div>
            <p className={`text-xl font-bold ${(mgPosition?.net_position || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(mgPosition?.net_position || 0))}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {(mgPosition?.net_position || 0) >= 0 ? 'Net receivable' : 'Net payable'}
            </p>
          </div>

          {/* You Owe */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">You Owe</span>
              <TrendingDown className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-xl font-bold text-red-600">
              {formatCurrency(mgPosition?.total_owed_to_others || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              To {mgPosition?.owes_to?.length || 0} MG(s)
            </p>
          </div>

          {/* Owed to You */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">Owed to You</span>
              <TrendingUp className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-xl font-bold text-green-600">
              {formatCurrency(mgPosition?.total_receivable_from_others || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              From {mgPosition?.owed_by?.length || 0} MG(s)
            </p>
          </div>

          {/* Float Available */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">Float Available</span>
              <Shield className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xl font-bold text-blue-600">
              {formatCurrency(floatLimit?.available_float || 0)}
            </p>
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{formatCurrency(floatLimit?.current_exposure || 0)} used</span>
                <span>{formatCurrency(floatLimit?.float_limit || 5000000)} limit</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${
                    ((floatLimit?.current_exposure || 0) / (floatLimit?.float_limit || 5000000)) > 0.8
                      ? 'bg-red-500'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    width: `${Math.min(100, ((floatLimit?.current_exposure || 0) / (floatLimit?.float_limit || 5000000)) * 100)}%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* UFCC Commission Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-5 mb-6 text-white">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-5 h-5" />
                <h2 className="text-lg font-bold">UFCC Commission</h2>
              </div>
              <p className="text-blue-100 text-sm">5% commission on all Katale transactions owed to UFCC</p>
            </div>
            <div className="flex gap-3">
              <div className="bg-white/15 rounded-lg px-4 py-3 backdrop-blur-sm">
                <p className="text-blue-100 text-xs uppercase tracking-wide">Pending</p>
                <p className="text-xl font-bold">{formatCurrency(ufccCommission.pending || 0)}</p>
              </div>
              <div className="bg-white/15 rounded-lg px-4 py-3 backdrop-blur-sm">
                <p className="text-blue-100 text-xs uppercase tracking-wide">Paid</p>
                <p className="text-xl font-bold">{formatCurrency(ufccCommission.paid || 0)}</p>
              </div>
            </div>
          </div>
          {(ufccCommission.pending || 0) > 0 && (
            <div className="mt-4 pt-3 border-t border-white/20">
              <button
                onClick={() => {
                  setUfccSettleForm({ ...ufccSettleForm, amount: ufccCommission.pending })
                  setShowUfccSettleModal(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-medium text-sm"
              >
                <Send className="w-4 h-4" />
                Settle Commission
              </button>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          {[
            { id: 'positions', label: 'Positions', icon: <ArrowLeftRight className="w-4 h-4" /> },
            { id: 'batches', label: 'Settlement Batches', icon: <FileText className="w-4 h-4" /> },
            { id: 'commission', label: 'Commission History', icon: <Banknote className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ===== POSITIONS TAB ===== */}
        {activeTab === 'positions' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* What You Owe */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-red-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  Amounts You Owe
                </h3>
                <p className="text-xs text-gray-500 mt-1">Net amounts your MG owes after offsetting incoming payments</p>
              </div>
              <div className="p-5">
                {mgPosition?.owes_to?.length > 0 ? (
                  <div className="space-y-3">
                    {mgPosition.owes_to.map((item, index) => {
                      const cp = item.counterparty_mg_code
                      // Batches where WE pay THEM (outgoing)
                      const outgoingPending = settlementBatches.filter(
                        b => b.from_mg_code === mgKey && b.to_mg_code === cp && b.status === 'pending'
                      )
                      const outgoingTotal = outgoingPending.reduce((sum, b) => sum + parseFloat(b.net_amount || 0), 0)
                      // Batches where THEY pay US (incoming offset)
                      const incomingPending = settlementBatches.filter(
                        b => b.from_mg_code === cp && b.to_mg_code === mgKey && b.status === 'pending'
                      )
                      const incomingTotal = incomingPending.reduce((sum, b) => sum + parseFloat(b.net_amount || 0), 0)

                      return (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                              <Building className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{cp}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Net position across all transactions</p>
                            </div>
                          </div>
                          <p className="font-bold text-xl text-red-600">{formatCurrency(item.amount_owed)}</p>
                        </div>

                        {/* Batch breakdown */}
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-xs">
                          {outgoingPending.length > 0 && (
                            <div className="flex justify-between text-gray-600">
                              <span className="flex items-center gap-1">
                                <TrendingDown className="w-3 h-3 text-red-400" />
                                Rolling batch (you → {cp})
                              </span>
                              <span className="font-medium text-red-600">{formatCurrency(outgoingTotal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-gray-800 font-medium pt-1 border-t border-dashed border-gray-300">
                            <span>Net owed</span>
                            <span className="text-red-600">{formatCurrency(item.amount_owed)}</span>
                          </div>
                        </div>

                        {outgoingPending.length > 0 && (
                          <button
                            onClick={() => { setActiveTab('batches'); setBatchFilter('pending') }}
                            className="mt-3 w-full text-center text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors"
                          >
                            <FileText className="w-3 h-3" />
                            View & settle batch
                          </button>
                        )}
                      </div>
                    )})}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">All clear!</p>
                    <p className="text-sm">No pending amounts owed</p>
                  </div>
                )}
              </div>
            </div>

            {/* What's Owed to You */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-green-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Amounts Owed to You
                </h3>
                <p className="text-xs text-gray-500 mt-1">Net amounts other MGs owe you after offsetting your payments</p>
              </div>
              <div className="p-5">
                {mgPosition?.owed_by?.length > 0 ? (
                  <div className="space-y-3">
                    {mgPosition.owed_by.map((item, index) => {
                      const cp = item.counterparty_mg_code
                      // Batches where THEY pay US
                      const incomingPending = settlementBatches.filter(
                        b => b.from_mg_code === cp && b.to_mg_code === mgKey && b.status === 'pending'
                      )
                      const incomingTotal = incomingPending.reduce((sum, b) => sum + parseFloat(b.net_amount || 0), 0)
                      // Batches where WE pay THEM (our offset)
                      const outgoingPending = settlementBatches.filter(
                        b => b.from_mg_code === mgKey && b.to_mg_code === cp && b.status === 'pending'
                      )
                      const outgoingTotal = outgoingPending.reduce((sum, b) => sum + parseFloat(b.net_amount || 0), 0)
                      const needsMyConfirm = incomingPending.filter(b => !b.confirmed_by_to_mg).length

                      return (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <Building className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{cp}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Net position across all transactions</p>
                            </div>
                          </div>
                          <p className="font-bold text-xl text-green-600">{formatCurrency(item.amount_owed_to_us)}</p>
                        </div>

                        {/* Batch breakdown */}
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-xs">
                          {incomingPending.length > 0 && (
                            <div className="flex justify-between text-gray-600">
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3 text-green-400" />
                                Rolling batch ({cp} → you)
                              </span>
                              <span className="font-medium text-green-600">{formatCurrency(incomingTotal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-gray-800 font-medium pt-1 border-t border-dashed border-gray-300">
                            <span>Net receivable</span>
                            <span className="text-green-600">{formatCurrency(item.amount_owed_to_us)}</span>
                          </div>
                        </div>

                        {needsMyConfirm > 0 && (
                          <button
                            onClick={() => { setActiveTab('batches'); setBatchFilter('pending') }}
                            className="mt-3 w-full text-center text-xs text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 rounded-md py-1.5 flex items-center justify-center gap-1 transition-colors"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Batch needs your confirmation
                          </button>
                        )}
                      </div>
                    )})}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Info className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="font-medium">No receivables</p>
                    <p className="text-sm">No other MGs owe you money yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== BATCHES TAB ===== */}
        {activeTab === 'batches' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Settlement Batches
                </h3>
                <p className="text-sm text-gray-500 mt-1">One rolling batch per MG pair — amount updates with each new transaction until settled</p>
              </div>
            </div>

            {/* Filter pills */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 text-sm flex-wrap">
              <span className="text-gray-500">Filter:</span>
              {['all', 'pending', 'settled'].map(f => (
                <button
                  key={f}
                  onClick={() => setBatchFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    batchFilter === f
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f !== 'all' && ` (${settlementBatches.filter(b => b.status === f).length})`}
                </button>
              ))}
              <span className="text-gray-400 mx-2">|</span>
              <span className="text-gray-500">
                {settlementBatches.length} total batch{settlementBatches.length !== 1 ? 'es' : ''}
              </span>
            </div>

            {(() => {
              const filtered = batchFilter === 'all' 
                ? settlementBatches 
                : settlementBatches.filter(b => b.status === batchFilter)
              
              return filtered.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filtered.map((batch) => {
                  const isFromMg = batch.from_mg_code === mgKey
                  const isToMg = batch.to_mg_code === mgKey
                  const canConfirm = (isFromMg && !batch.confirmed_by_from_mg) || (isToMg && !batch.confirmed_by_to_mg)
                  const isExpanded = expandedBatch === batch.id
                  const txnCount = parseInt(batch.transaction_count || batch.order_count || 0)

                  return (
                    <div key={batch.id}>
                      {/* Batch Row */}
                      <div className="px-5 py-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => setExpandedBatch(isExpanded ? null : batch.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </button>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">
                                  {batch.batch_number || `Batch #${batch.id}`}
                                </span>
                                {getStatusBadge(batch.status)}
                                {txnCount > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                    {txnCount} transaction{txnCount !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 flex-wrap">
                                <span className={isFromMg ? 'font-medium text-red-600' : ''}>{batch.from_mg_code}</span>
                                <span className="text-gray-400">pays</span>
                                <span className={isToMg ? 'font-medium text-green-600' : ''}>{batch.to_mg_code}</span>
                                <span className="text-gray-300">|</span>
                                <span>{formatDate(batch.settlement_period_start)} – {formatDate(batch.settlement_period_end)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-gray-900">{formatCurrency(batch.net_amount)}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs ${batch.confirmed_by_from_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {batch.confirmed_by_from_mg ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                  From
                                </span>
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs ${batch.confirmed_by_to_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {batch.confirmed_by_to_mg ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                  To
                                </span>
                              </div>
                            </div>

                            {canConfirm && batch.status !== 'settled' && (
                              <button
                                onClick={() => {
                                  setSelectedBatch(batch)
                                  setShowConfirmModal(true)
                                }}
                                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                                Confirm
                              </button>
                            )}
                            {batch.status === 'settled' && (
                              <span className="text-green-600 text-sm flex items-center gap-1">
                                <CheckCircle2 className="w-4 h-4" />
                                Settled
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Net Settlement Amount</p>
                              <p className="font-bold text-lg text-gray-900">{formatCurrency(batch.net_amount)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Transactions</p>
                              <p className="font-medium text-gray-900">{txnCount} transaction{txnCount !== 1 ? 's' : ''} netted</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Period</p>
                              <p className="font-medium text-gray-900">{formatDate(batch.settlement_period_start)} – {formatDate(batch.settlement_period_end)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payer</p>
                              <p className="font-medium text-red-600">{batch.from_mg_code}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Receiver</p>
                              <p className="font-medium text-green-600">{batch.to_mg_code}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Settled At</p>
                              <p className="font-medium text-gray-900">{batch.settled_at ? formatDateTime(batch.settled_at) : '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Bank Reference</p>
                              <p className="font-medium text-gray-900">{batch.bank_reference || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Mobile Money Reference</p>
                              <p className="font-medium text-gray-900">{batch.mobile_money_reference || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Confirmations</p>
                              <p className="font-medium text-gray-900">
                                {batch.from_mg_code}: {batch.confirmed_by_from_mg ? '✅ ' + formatDateTime(batch.confirmed_by_from_mg_at) : '⏳ Pending'}
                                <br />
                                {batch.to_mg_code}: {batch.confirmed_by_to_mg ? '✅ ' + formatDateTime(batch.confirmed_by_to_mg_at) : '⏳ Pending'}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              <Info className="w-3 h-3 inline mr-1" />
                              This batch reflects the net of all transactions between {batch.from_mg_code} and {batch.to_mg_code}. 
                              The amount updates automatically as new orders are placed. Once both parties confirm, all transactions are settled.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">{batchFilter === 'all' ? 'No settlement batches yet' : `No ${batchFilter} batches`}</p>
                <p className="text-sm">A rolling batch is created per MG pair when cross-MG orders occur</p>
              </div>
            )
            })()}
          </div>
        )}

        {/* ===== COMMISSION HISTORY TAB ===== */}
        {activeTab === 'commission' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-blue-500" />
                UFCC Commission Transactions
              </h3>
              <p className="text-sm text-gray-500 mt-1">History of 5% commission charges on your MG's Katale transactions</p>
            </div>

            {/* Summary bar */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-6 text-sm">
              <div>
                <span className="text-gray-500">Total: </span>
                <span className="font-bold text-gray-900">{formatCurrency(ufccCommission.total || ((ufccCommission.pending || 0) + (ufccCommission.paid || 0)))}</span>
              </div>
              <div>
                <span className="text-gray-500">Pending: </span>
                <span className="font-bold text-yellow-600">{formatCurrency(ufccCommission.pending || 0)}</span>
              </div>
              <div>
                <span className="text-gray-500">Paid: </span>
                <span className="font-bold text-green-600">{formatCurrency(ufccCommission.paid || 0)}</span>
              </div>
            </div>

            {ufccCommission.transactions?.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {ufccCommission.transactions.map((txn, index) => (
                  <div key={index} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txn.is_settled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        {txn.is_settled ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Order #{txn.order_id}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(txn.order_date || txn.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatCurrency(txn.commission_amount)}</p>
                      <p className={`text-xs ${txn.is_settled ? 'text-green-600' : 'text-yellow-600'}`}>
                        {txn.is_settled ? 'Settled' : 'Pending'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Banknote className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">No commission transactions</p>
                <p className="text-sm">Commission charges will appear here after Katale orders are placed</p>
              </div>
            )}
          </div>
        )}

        {/* How Settlement Works */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-4">
            <Info className="w-5 h-5" />
            How Settlement Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { step: 1, title: 'Net Position Tracked', desc: 'Every cross-MG order updates the net position between two MGs automatically' },
              { step: 2, title: 'One Batch Per Pair', desc: 'One rolling batch per MG pair — the amount reflects the current net owed' },
              { step: 3, title: 'Make Payment', desc: 'When ready, transfer the net amount via bank or mobile money' },
              { step: 4, title: 'Both MGs Confirm', desc: 'Both parties confirm to settle all transactions at once, resetting the balance' }
            ].map(item => (
              <div key={item.step} className="bg-white rounded-lg p-3">
                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-blue-600 font-bold text-sm">{item.step}</span>
                </div>
                <h4 className="font-medium text-gray-900 text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== CREATE BATCH MODAL ===== */}
      {showCreateBatchModal && selectedCounterparty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Settlement Batch</h3>
              <button onClick={() => { setShowCreateBatchModal(false); setSelectedCounterparty(null) }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600 font-medium">Settlement to {selectedCounterparty.counterparty_mg_code}</p>
              <p className="font-bold text-2xl text-red-700 mt-1">{formatCurrency(selectedCounterparty.amount_owed)}</p>
              <p className="text-xs text-red-500 mt-1">This will create or update a rolling batch with the current net position</p>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <Info className="w-3 h-3 inline mr-1" />
                Batches are normally auto-created at checkout. Use this if a batch doesn't exist yet for this MG pair.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowCreateBatchModal(false); setSelectedCounterparty(null) }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBatch}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Batch
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CONFIRM SETTLEMENT MODAL ===== */}
      {showConfirmModal && selectedBatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Settlement</h3>
              <button onClick={() => setShowConfirmModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">{selectedBatch.batch_number || `Batch #${selectedBatch.id}`}</p>
              <p className="font-bold text-xl">{formatCurrency(selectedBatch.net_amount)}</p>
              <p className="text-sm text-gray-500">{selectedBatch.from_mg_code} → {selectedBatch.to_mg_code}</p>
            </div>

            {/* Payment Method Toggle */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmForm({ ...confirmForm, payment_method: 'bank' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    confirmForm.payment_method === 'bank'
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Bank Transfer
                </button>
                <button
                  onClick={() => setConfirmForm({ ...confirmForm, payment_method: 'mobile_money' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    confirmForm.payment_method === 'mobile_money'
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile Money
                </button>
              </div>
            </div>

            {/* Reference Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {confirmForm.payment_method === 'bank' ? 'Bank Reference Number' : 'Mobile Money Reference'}
              </label>
              <input
                type="text"
                value={confirmForm.payment_method === 'bank' ? confirmForm.bank_reference : confirmForm.mobile_money_reference}
                onChange={(e) => {
                  const field = confirmForm.payment_method === 'bank' ? 'bank_reference' : 'mobile_money_reference'
                  setConfirmForm({ ...confirmForm, [field]: e.target.value })
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={confirmForm.payment_method === 'bank' ? 'e.g., TRX123456789' : 'e.g., MM123456789'}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettlement}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Confirming...' : 'Confirm Settlement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== UFCC SETTLEMENT MODAL ===== */}
      {showUfccSettleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Settle UFCC Commission</h3>
              <button onClick={() => setShowUfccSettleModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">UFCC Commission Payment</p>
              <p className="font-bold text-xl">{formatCurrency(ufccCommission.pending)}</p>
              <p className="text-xs text-gray-500 mt-1">5% commission from Katale transactions</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount to Settle</label>
              <input
                type="number"
                value={ufccSettleForm.amount}
                onChange={(e) => setUfccSettleForm({ ...ufccSettleForm, amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter amount"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setUfccSettleForm({ ...ufccSettleForm, payment_method: 'bank' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    ufccSettleForm.payment_method === 'bank'
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Bank
                </button>
                <button
                  onClick={() => setUfccSettleForm({ ...ufccSettleForm, payment_method: 'mobile_money' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    ufccSettleForm.payment_method === 'mobile_money'
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile Money
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {ufccSettleForm.payment_method === 'bank' ? 'Bank Reference Number' : 'Mobile Money Reference'}
              </label>
              <input
                type="text"
                value={ufccSettleForm.payment_method === 'bank' ? ufccSettleForm.bank_reference : ufccSettleForm.mobile_money_reference}
                onChange={(e) => {
                  const field = ufccSettleForm.payment_method === 'bank' ? 'bank_reference' : 'mobile_money_reference'
                  setUfccSettleForm({ ...ufccSettleForm, [field]: e.target.value })
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={ufccSettleForm.payment_method === 'bank' ? 'e.g., TRX123456789' : 'e.g., MM123456789'}
              />
            </div>

            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                <Info className="w-3 h-3 inline mr-1" />
                UFCC Bank Account: <strong>Bank of Uganda - 0123456789</strong><br />
                Mobile Money: <strong>0700 123 456</strong>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUfccSettleModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSettleUfcc}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KataleSettlements
